/**
 * 🧮 원가분석표 한 장 고치기 — 원가분석 탭(오른쪽)과 견적서 작성 창의 🧮 원가분석 창이 같이 쓴다 (v2.248.0 에서 탭에서 떼어 냄)
 *
 * 머리(제목·고객·메모·마진율·올림) · 줄 표 · 합계 타일 · 제안가 정하기(시나리오·목표 금액) · 가져오기 창.
 *   줄은 세 가지 길로 들어온다 — [+ 매입 목록에서] · [+ 인포트렌드 가격표에서] · [+ 직접 입력].
 *   판매 단가는 마진율로 자동(원가 ÷ (1 − 마진율) 올림) · 줄마다 직접 적으면 그 값(↺ 로 자동).
 * compact = 떠 있는 창 — 「출처」·「매입처」 칸은 품목명 칸 안으로, 「원가 금액」·「판매 금액·마진율」은 단가 칸 아래 줄로 접어
 *   표 폭을 줄인다(같은 값 · 같은 입력 · 10-13 실측: 접기 전 957px → 1536 화면의 창(≈780px)에서 옆 스크롤).
 * 계산은 전부 lib/cost-sheet-calc.ts(그물 check:cost-sheet) · 저장은 useCostSheets(0.7초 뒤 자동).
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import type { CostSheet } from '../../lib/cost-sheet-api';
import {
  MARGIN_MAX, ROUND_UNITS, SCENARIO_MARGINS, SOURCE_LABEL, appliedUnitCost, emptyLine, fmtKrw, fmtPct, lineCost,
  lineMarginPct, lineSell, lineSellUnit, sellAtMargin, sheetTotals, solveMarginForTarget,
  type CostLine, type CostSource,
} from '../../lib/cost-sheet-calc';
import { fetchRecentSupplier } from '../../lib/stock-api';
import { DraggableModal } from '../DraggableModal';
import { SegmentedControl } from '../ui/SegmentedControl';
import { NumCell, TextCell, Tile } from './CostUi';
import { InfortrendPicker, PurchasePicker } from './LinePickers';
import type { CostSheetStore } from './useCostSheets';

const SOURCE_BADGE: Readonly<Record<CostSource, string>> = {
  purchase: 'bg-blue-100 text-blue-700',
  infortrend: 'bg-violet-100 text-violet-700',
  manual: 'bg-slate-100 text-slate-600',
};

const fmtClock = (t: number): string => {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function CostSheetEditor({ sheet, store, canEdit, compact = false, titleRef, headerActions, footer }: {
  sheet: CostSheet;
  store: CostSheetStore;
  canEdit: boolean;
  /** 떠 있는 창 — 출처·매입처를 품목명 칸 안으로 접는다 */
  compact?: boolean;
  titleRef?: Ref<HTMLInputElement>;
  /** 머리 오른쪽 단추들(탭: 영업건 · 견적서 · 복사 · 삭제) */
  headerActions?: ReactNode;
  /** 맨 아래(탭: [📄 견적서 작성으로] · 창: [⬅ 견적서에 반영]) */
  footer?: ReactNode;
}) {
  const { patchSheet, flush, currentLines } = store;
  const id = sheet.id;
  const status = store.saveState && store.saveState.id === id ? store.saveState : null;

  /* ── 줄 고치기 — 항상 캐시의 최신 줄에서 출발(같은 순간 두 칸 고침 · 10-09) ── */
  const setLines = (next: CostLine[]): void => patchSheet(id, { lines: next });
  const updateLine = (key: string, patch: Partial<CostLine>): void => {
    setLines(currentLines(id).map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const removeLine = (key: string): void => setLines(currentLines(id).filter((l) => l.key !== key));
  const moveLine = (key: string, dir: -1 | 1): void => {
    const lines = currentLines(id);
    const idx = lines.findIndex((l) => l.key === key);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= lines.length) return;
    const next = [...lines];
    const [row] = next.splice(idx, 1);
    next.splice(to, 0, row!);
    setLines(next);
  };
  const newRowRef = useRef<string | null>(null);
  const nameRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const addManual = (): void => {
    const l = emptyLine('manual');
    newRowRef.current = l.key;
    setLines([...currentLines(id), l]);
  };
  useEffect(() => {
    if (!newRowRef.current) return;
    const el = nameRefs.current.get(newRowRef.current);
    if (el) { el.focus(); newRowRef.current = null; }
  });
  /** ↺ 전부 자동 — 직접 적은(또는 견적서에서 따라온) 판매 단가를 모두 마진율 자동으로 */
  const fixedCount = sheet.lines.filter((l) => l.sellUnit !== null).length;
  const unfixAll = (): void => {
    if (!window.confirm(`판매 단가를 직접 정해 둔 줄 ${fixedCount}개를 모두 마진율 자동으로 바꿀까요?\n(그 줄의 판매 단가가 「원가 ÷ (1 − 마진율)」 로 다시 계산됩니다)`)) return;
    setLines(currentLines(id).map((l) => (l.sellUnit !== null ? { ...l, sellUnit: null } : l)));
  };

  const [picker, setPicker] = useState<'purchase' | 'infortrend' | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshPurchasePrices = async (): Promise<void> => {
    if (refreshing) return;
    const targets = sheet.lines.filter((l) => l.source === 'purchase' && l.itemCode);
    if (targets.length === 0) { window.alert('매입 목록에서 가져온 줄이 없습니다.'); return; }
    setRefreshing(true);
    try {
      const infos = await Promise.all(targets.map((l) => fetchRecentSupplier(l.itemCode!).catch(() => null)));
      const byKey = new Map(targets.map((l, i) => [l.key, infos[i]] as const));
      let changed = 0;
      const next = currentLines(id).map((l) => {
        const info = byKey.get(l.key);
        if (!info || !info.count) return l;
        const patch: Partial<CostLine> = { recent: info.recentPrice || l.recent, avg: info.avgPrice || l.avg, vendor: l.vendor || info.supplier };
        if (patch.recent !== l.recent || patch.avg !== l.avg) changed += 1;
        return { ...l, ...patch };
      });
      setLines(next);
      window.alert(changed > 0 ? `매입가가 바뀐 줄 ${changed}개를 갱신했습니다.` : '바뀐 매입가가 없습니다.');
    } finally {
      setRefreshing(false);
    }
  };

  const totals = sheetTotals(sheet.lines, sheet);
  const cellInput = 'w-full border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white';
  const subInput = 'mt-1 w-full border border-slate-200 rounded px-2 py-0.5 text-xs text-slate-600 bg-slate-50/70 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:bg-white';

  return (
    <div className="space-y-4">
      {/* 머리 — 제목·고객·메모·마진율 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[16rem]">
            <TextCell ref={titleRef} value={sheet.title} readOnly={!canEdit} maxLength={200}
              onChange={(t) => patchSheet(id, { title: t })}
              placeholder="제목 (사업명 · 고객 프로젝트)"
              className="w-full text-lg font-bold text-slate-900 border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-1 bg-transparent" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {status?.status === 'saving' && <span className="text-xs text-slate-500">저장 중…</span>}
            {status?.status === 'saved' && <span className="text-xs text-emerald-600">저장됨 {fmtClock(status.at)}</span>}
            {status?.status === 'error' && (
              <span className="text-xs text-red-600 flex items-center gap-1">
                저장 실패{status.message ? ` — ${status.message}` : ''}
                <button type="button" onClick={() => { void flush(id); }} className="underline">다시 저장</button>
              </span>
            )}
            {headerActions}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-x-4 gap-y-2 mt-3 items-end">
          <label className="ui-label">
            <span className="block text-xs text-slate-500 mb-0.5">고객 · 엔드유저</span>
            <TextCell value={sheet.customer} readOnly={!canEdit} maxLength={200} onChange={(t) => patchSheet(id, { customer: t })}
              placeholder="예: 강남구청" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm" />
          </label>
          <label className="ui-label">
            <span className="block text-xs text-slate-500 mb-0.5">메모</span>
            <TextCell value={sheet.memo} readOnly={!canEdit} maxLength={4000} onChange={(t) => patchSheet(id, { memo: t })}
              placeholder="조건 · 납기 · 참고" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm" />
          </label>
          <label className="ui-label">
            <span className="block text-xs text-slate-500 mb-0.5">마진율 <span className="text-slate-400">(판매가 기준)</span></span>
            <span className="inline-flex items-center gap-1">
              <NumCell value={sheet.marginPct} step="any" readOnly={!canEdit} className="w-20"
                onChange={(n) => patchSheet(id, { marginPct: Math.min(MARGIN_MAX, n) })} title={`0 ~ ${MARGIN_MAX}%`} />
              <span className="text-sm text-slate-500">%</span>
            </span>
          </label>
          <label className="ui-label">
            <span className="block text-xs text-slate-500 mb-0.5">판매 단가 올림</span>
            <select value={sheet.roundUnit} disabled={!canEdit} onChange={(e) => patchSheet(id, { roundUnit: Number(e.target.value) })}
              className="ui-input">
              {ROUND_UNITS.map((u) => <option key={u} value={u}>{u.toLocaleString('ko-KR')}원</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* 줄 표 */}
      <div className="bg-white rounded shadow-sm border border-slate-200">
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200 flex-wrap">
          <div>
            <h3 className="ui-subsection-title">원가 구성 <span className="text-sm font-normal text-slate-500">{sheet.lines.length}줄</span></h3>
            <p className="text-xs text-slate-500 mt-0.5">원가 단가는 매입 줄이면 최근/평균/직접 중 고르고, 판매 단가는 마진율로 자동 — 직접 적으면 그 값이 쓰입니다(↺ 로 자동으로).</p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setPicker('purchase')} className="btn btn-primary">+ 매입 목록에서</button>
              <button type="button" onClick={() => setPicker('infortrend')} className="btn btn-primary">+ 인포트렌드 가격표에서</button>
              <button type="button" onClick={addManual} className="btn btn-secondary">+ 직접 입력</button>
              <button type="button" onClick={() => { void refreshPurchasePrices(); }} disabled={refreshing}
                className={`btn ${refreshing ? 'btn-secondary' : 'btn-success'}`}
                title="매입 목록에서 가져온 줄의 최근·평균 매입가를 다시 읽어 옵니다">
                {refreshing ? '갱신 중…' : '🔄 최근 매입가 갱신'}
              </button>
              {fixedCount > 0 && (
                <button type="button" onClick={unfixAll}
                  className="btn btn-warning"
                  title="판매 단가를 직접 정해 둔 줄(견적서에서 따라온 단가 포함)을 모두 마진율 자동으로 되돌립니다">
                  ↺ 판매 단가 전부 자동 ({fixedCount})
                </button>
              )}
            </div>
          )}
        </div>

        {sheet.lines.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            <div className="text-3xl mb-2">📦</div>
            아직 줄이 없습니다 — 위 단추로 매입 목록·인포트렌드 가격표에서 가져오거나 직접 적으세요.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              {/* 폭: 1920 화면(목록 19rem 빼고 ≈1,250px)에 옆 스크롤 없이 들어가게 — 마진율은 판매 금액 밑 작은 글씨 (10-09 실측)
                  compact(떠 있는 창) 는 출처·매입처를 품목명 칸 안으로 접는다 */}
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-1.5 py-2 text-center w-8">#</th>
                  {!compact && <th className="px-1.5 py-2 text-left w-20">출처</th>}
                  <th className={`px-1.5 py-2 text-left ${compact ? 'min-w-[9rem]' : 'min-w-[10rem]'}`}>품목명{compact && <span className="block text-[11px] font-normal text-slate-500">출처 · 매입처</span>}</th>
                  <th className={`px-1.5 py-2 text-left ${compact ? 'min-w-[9rem]' : 'min-w-[10rem]'}`}>규격 · 설명</th>
                  {!compact && <th className="px-1.5 py-2 text-left min-w-[6.5rem]">매입처</th>}
                  <th className="px-1.5 py-2 text-right w-16">수량</th>
                  <th className="px-1.5 py-2 text-right">원가 단가{compact && <span className="block text-[11px] font-normal text-slate-500">= 원가 금액</span>}</th>
                  {!compact && <th className="px-1.5 py-2 text-right w-24">원가 금액</th>}
                  <th className="px-1.5 py-2 text-right">판매 단가{compact && <span className="block text-[11px] font-normal text-slate-500">= 판매 금액 · 마진율</span>}</th>
                  {!compact && <th className="px-1.5 py-2 text-right w-24">판매 금액<span className="block text-[11px] font-normal text-slate-500">마진율</span></th>}
                  <th className="px-1 py-2 w-14"></th>
                </tr>
              </thead>
              <tbody>
                {sheet.lines.map((l, idx) => {
                  const hasStats = l.recent !== null || l.avg !== null;
                  const applied = appliedUnitCost(l);
                  const sellUnit = lineSellUnit(l, sheet);
                  const mp = lineMarginPct(l, sheet);
                  const badge = <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${SOURCE_BADGE[l.source]}`}>{SOURCE_LABEL[l.source]}</span>;
                  const vendorInput = (
                    <TextCell value={l.vendor} readOnly={!canEdit} maxLength={200} onChange={(t) => updateLine(l.key, { vendor: t })} placeholder="매입처" title={l.vendor}
                      className={compact ? subInput : cellInput} />
                  );
                  return (
                    <tr key={l.key} className="border-t border-slate-100 align-top hover:bg-slate-50/60">
                      <td className="px-1.5 py-1.5 text-center text-slate-500 pt-2.5">{idx + 1}</td>
                      {!compact && (
                        <td className="px-1.5 py-1.5">
                          {badge}
                          {l.source === 'purchase' && l.itemCode && <div className="text-xs text-slate-500 font-mono mt-0.5 truncate max-w-[5rem]" title={l.itemCode}>{l.itemCode}</div>}
                          {l.source === 'infortrend' && l.quarter && <div className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{l.quarter}</div>}
                        </td>
                      )}
                      <td className="px-1.5 py-1.5">
                        <TextCell ref={(el) => { if (el) nameRefs.current.set(l.key, el); else nameRefs.current.delete(l.key); }}
                          value={l.name} readOnly={!canEdit} maxLength={300} onChange={(t) => updateLine(l.key, { name: t })} placeholder="품목명" className={cellInput} />
                        {compact ? (
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-500 min-w-0">
                            {badge}
                            <span className="font-mono truncate" title={l.pn ?? l.itemCode ?? l.quarter ?? ''}>{l.pn ?? (l.source === 'purchase' ? l.itemCode : l.quarter) ?? ''}</span>
                          </div>
                        ) : l.pn && <div className="text-xs text-slate-500 font-mono mt-0.5 truncate" title={l.pn}>{l.pn}</div>}
                        {compact && vendorInput}
                      </td>
                      <td className="px-1.5 py-1.5">
                        <TextCell value={l.spec} readOnly={!canEdit} maxLength={1000} onChange={(t) => updateLine(l.key, { spec: t })} placeholder="규격 · 설명" title={l.spec} className={cellInput} />
                        {/* 비고 — 견적서로 넘길 때 그 줄의 비고 칸에 들어간다 */}
                        <TextCell value={l.note} readOnly={!canEdit} maxLength={500} onChange={(t) => updateLine(l.key, { note: t })} placeholder="비고 (견적서 비고 칸으로)" title={l.note}
                          className={subInput} />
                      </td>
                      {!compact && <td className="px-1.5 py-1.5">{vendorInput}</td>}
                      <td className="px-1.5 py-1.5 text-right">
                        <NumCell value={l.qty} readOnly={!canEdit} step="any" className="w-16" onChange={(n) => updateLine(l.key, { qty: n })} />
                      </td>
                      <td className="px-1.5 py-1.5 text-right">
                        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                          {hasStats && (
                            <select value={l.basis} disabled={!canEdit} title="원가 기준 — 최근 매입가 · 평균 매입가 · 직접"
                              onChange={(e) => {
                                const basis = e.target.value as CostLine['basis'];
                                updateLine(l.key, { basis, unitCost: basis === 'manual' ? applied : l.unitCost });
                              }}
                              className="ui-input ui-input-sm">
                              <option value="recent">최근</option>
                              <option value="avg">평균</option>
                              <option value="manual">직접</option>
                            </select>
                          )}
                          <NumCell value={applied} readOnly={!canEdit || (hasStats && l.basis !== 'manual')} step="any" className="w-24"
                            onChange={(n) => updateLine(l.key, { unitCost: n, basis: 'manual' })} />
                        </div>
                        {hasStats && (
                          <div className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">최근 {fmtKrw(l.recent)} · 평균 {fmtKrw(l.avg)}</div>
                        )}
                        {l.source === 'infortrend' && l.listPrice !== null && (
                          <div className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">소비자가 {fmtKrw(l.listPrice)}</div>
                        )}
                        {compact && <div className="text-xs text-slate-600 mt-0.5 tabular-nums whitespace-nowrap">= {fmtKrw(lineCost(l))}</div>}
                      </td>
                      {!compact && <td className="px-1.5 py-1.5 text-right tabular-nums pt-2.5 whitespace-nowrap">{fmtKrw(lineCost(l))}</td>}
                      <td className="px-1.5 py-1.5 text-right">
                        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                          {l.sellUnit !== null ? (
                            <button type="button" onClick={() => updateLine(l.key, { sellUnit: null })} title="마진율 자동으로 되돌리기"
                              className="btn btn-warning btn-sm">↺</button>
                          ) : (
                            <span className="text-[11px] text-slate-500 px-0.5 whitespace-nowrap" title="마진율로 자동 계산">자동</span>
                          )}
                          <NumCell value={sellUnit} readOnly={!canEdit} step="any" className={`w-24 ${l.sellUnit !== null ? 'border-amber-300' : ''}`}
                            onChange={(n) => updateLine(l.key, { sellUnit: n })} />
                        </div>
                        {compact && (
                          <div className="text-xs mt-0.5 tabular-nums whitespace-nowrap">
                            = <b>{fmtKrw(lineSell(l, sheet))}</b> · <span className={mp !== null && mp < 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}>{fmtPct(mp)}</span>
                          </div>
                        )}
                      </td>
                      {!compact && (
                        <td className="px-1.5 py-1.5 text-right tabular-nums pt-2.5 whitespace-nowrap">
                          <div className="font-semibold">{fmtKrw(lineSell(l, sheet))}</div>
                          <div className={`text-xs ${mp !== null && mp < 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>{fmtPct(mp)}</div>
                        </td>
                      )}
                      <td className="px-1 py-1.5 text-right whitespace-nowrap text-xs">
                        {canEdit && (
                          <>
                            <button type="button" onClick={() => moveLine(l.key, -1)} disabled={idx === 0} title="위로" className="text-slate-400 hover:text-slate-700 disabled:opacity-30 px-0.5">▲</button>
                            <button type="button" onClick={() => moveLine(l.key, 1)} disabled={idx === sheet.lines.length - 1} title="아래로" className="text-slate-400 hover:text-slate-700 disabled:opacity-30 px-0.5">▼</button>
                            <button type="button" onClick={() => removeLine(l.key)} title="줄 지우기" className="text-red-500 hover:bg-red-50 rounded px-0.5">🗑</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 합계 타일 */}
      <div className={`grid gap-3 ${compact ? 'grid-cols-2 sm:grid-cols-3 2xl:grid-cols-5' : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5'}`}>
        <Tile label="총 원가" hint="부가세 제외" value={fmtKrw(totals.cost)} />
        <Tile label="총 판매가 (공급가)" hint="부가세 제외" value={fmtKrw(totals.sell)} strong />
        <Tile label="마진" hint="판매 − 원가" value={fmtKrw(totals.margin)} tone={totals.margin < 0 ? 'bad' : 'good'} />
        <Tile label="마진율" hint="판매가 기준" value={fmtPct(totals.marginPct)} tone={totals.marginPct !== null && totals.marginPct < 0 ? 'bad' : 'good'} />
        <Tile label="부가세 포함" hint="판매가 × 1.1" value={fmtKrw(totals.sellWithVat)} />
      </div>
      {/* 제안가 정하기 — 마진율 시나리오 띠 · 목표 금액 맞추기 (v2.235.0 · 「견적서 쓸 때 제안가격 참고」) */}
      {sheet.lines.length > 0 && (
        <ProposalHelper
          lines={sheet.lines}
          marginPct={sheet.marginPct}
          roundUnit={sheet.roundUnit}
          canEdit={canEdit}
          stacked={compact}
          onApplyMargin={(m) => patchSheet(id, { marginPct: m })}
        />
      )}
      {footer}

      {picker && (
        <LinePicker
          initial={picker}
          onAdd={(line) => setLines([...currentLines(id), line])}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 제안가 정하기 — 마진율 시나리오 띠(10·15·20·25·30%) · 🎯 목표 금액 맞추기(거꾸로 마진율)
 *   사용자(10-09): 「견적서 작성할 때 제안가격 참고하려고 하는 건데」 — 제안가를 정할 때 가장 흔한 질문이
 *   「부가세 포함 5,000만 원에 맞추려면 마진율이 몇 %?」 라서, 올림 단위까지 반영해 넘는 쪽에서 가장 가까운 마진율을 찾는다.
 * ──────────────────────────────────────────────────────────────── */
function ProposalHelper({ lines, marginPct, roundUnit, canEdit, stacked, onApplyMargin }: {
  lines: CostLine[]; marginPct: number; roundUnit: number; canEdit: boolean; stacked?: boolean; onApplyMargin: (m: number) => void;
}) {
  const [target, setTarget] = useState(0);
  const [withVat, setWithVat] = useState(true);
  const targetSupply = withVat ? target / 1.1 : target;
  const solve = target > 0 ? solveMarginForTarget(lines, roundUnit, targetSupply) : null;
  const fixedCount = lines.filter((l) => l.sellUnit !== null).length;
  const scenarios = SCENARIO_MARGINS.map((m) => ({ m, sell: sellAtMargin(lines, roundUnit, m) }));
  const current = Math.round(marginPct * 100) / 100;

  return (
    <div className={`bg-white rounded shadow-sm border border-slate-200 p-4 grid grid-cols-1 gap-4 ${stacked ? '' : 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]'}`}>
      <div>
        <div className="text-sm font-bold text-slate-800">마진율 시나리오 <span className="text-xs font-normal text-slate-500">— 이 마진율이면 총 판매가(공급가)가</span></div>
        <div className="flex flex-wrap gap-2 mt-2">
          {scenarios.map(({ m, sell }) => {
            const active = current === m;
            return (
              <button key={m} type="button" disabled={!canEdit || active} onClick={() => onApplyMargin(m)} title={active ? '지금 마진율' : `마진율을 ${m}% 로 바꿉니다`}
                className={`btn tabular-nums disabled:cursor-default disabled:opacity-100 ${active ? 'btn-warning font-semibold' : 'btn-secondary'}`}>
                <span className={active ? '' : 'text-slate-500'}>{m}%</span> → <b>{fmtKrw(sell)}</b>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-slate-500 mt-1.5">
          지금 {fmtPct(marginPct, 2)} · 올림 단위 {roundUnit.toLocaleString('ko-KR')}원{fixedCount > 0 ? ` · 직접 적은 판매 단가 ${fixedCount}줄은 그대로` : ''} — 누르면 마진율이 그 값으로 바뀝니다
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-slate-800">🎯 목표 금액 맞추기 <span className="text-xs font-normal text-slate-500">— 이 금액에 맞추려면 마진율이 몇 %?</span></div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <NumCell value={target} step="any" className="w-40" onChange={setTarget} title="목표 총 판매가" />
          <span className="text-sm text-slate-600">원</span>
          <label className="inline-flex items-center gap-1 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={withVat} onChange={(e) => setWithVat(e.target.checked)} /> 부가세 포함 금액
          </label>
        </div>
        <div className="mt-2 text-sm min-h-[2.5rem]">
          {!solve ? (
            <span className="text-slate-400">금액을 적으면 올림 단위까지 반영해 넘는 쪽에서 가장 가까운 마진율을 찾습니다.</span>
          ) : solve.ok ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-slate-800">
                마진율 <b className="text-emerald-700">{fmtPct(solve.marginPct, 2)}</b> 로 하면 총 판매가 <b>{fmtKrw(solve.sell)}</b>원
                <span className="text-slate-500"> (부가세 포함 {fmtKrw(Math.round(solve.sell * 1.1))}원)</span>
              </span>
              {canEdit && (
                <button type="button" onClick={() => onApplyMargin(solve.marginPct)} disabled={current === solve.marginPct}
                  className="btn btn-success-solid btn-sm">
                  {current === solve.marginPct ? '적용됨' : '이 마진율로 적용'}
                </button>
              )}
            </div>
          ) : solve.reason === 'below_cost' ? (
            <span className="text-red-600">목표가 마진 0% 일 때의 판매가 {fmtKrw(solve.sell)}원(≈ 원가)보다 낮습니다 — 마진이 음수가 되어 맞출 수 없습니다.</span>
          ) : solve.reason === 'unreachable' ? (
            <span className="text-red-600">마진율 {MARGIN_MAX}% 로도 {fmtKrw(solve.sell)}원까지라 목표에 못 미칩니다.</span>
          ) : solve.reason === 'fixed_only' ? (
            <span className="text-amber-700">모든 줄의 판매 단가를 직접 적어 두어 마진율로는 움직이지 않습니다(지금 {fmtKrw(solve.sell)}원). ↺ 로 자동으로 되돌린 뒤 쓰세요.</span>
          ) : (
            <span className="text-slate-400">넣을 줄이 없습니다.</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 가져오기 창 — 매입 목록(품목 마스터 → 최근/평균 매입가 · 매입 이력) / 인포트렌드 가격표(현재 분기 검색)
 * ──────────────────────────────────────────────────────────────── */
function LinePicker({ initial, onAdd, onClose }: { initial: 'purchase' | 'infortrend'; onAdd: (line: CostLine) => void; onClose: () => void }) {
  const [source, setSource] = useState<'purchase' | 'infortrend'>(initial);
  const [added, setAdded] = useState<string[]>([]);
  const pushAdded = (name: string) => setAdded((a) => [...a, name]);

  return (
    <DraggableModal onClose={onClose} initialWidth={1040} initialHeight={760} bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col">
      <div className="dm-drag-handle flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white cursor-move select-none">
        <div className="flex items-center gap-3">
          <h3 className="ui-subsection-title">원가 가져오기</h3>
          <SegmentedControl ariaLabel="원가 가져올 곳" value={source} onChange={setSource}
            options={[{ key: 'purchase', label: '매입 목록' }, { key: 'infortrend', label: '인포트렌드 가격표' }]} />
        </div>
        <div className="flex items-center gap-3">
          {added.length > 0 && <span className="text-xs text-emerald-700">추가됨 {added.length}줄 · 마지막: {added[added.length - 1]}</span>}
          <button type="button" onClick={onClose} className="ui-close" title="닫기">✕</button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-5">
        {source === 'purchase' ? <PurchasePicker onAdd={(l) => { onAdd(l); pushAdded(l.name); }} /> : <InfortrendPicker onAdd={(l) => { onAdd(l); pushAdded(l.name); }} />}
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
        <span className="text-xs text-slate-500">
          {source === 'purchase'
            ? '품목은 [재고관리 ▸ 품명&품목] 마스터에서 찾고, 단가는 매입 내역의 최근·평균값을 가져옵니다(나라장터 BOM 과 같은 자료).'
            : '현재 분기 가격표에서 찾습니다(없으면 옛 분기까지). 공급가가 원가로 들어가고 소비자가는 참고로 남습니다.'}
        </span>
        <button type="button" onClick={onClose} className="btn btn-dark">닫기</button>
      </div>
    </DraggableModal>
  );
}
